/**
 * StageCockpit — главный «пульт этапа» карточки сделки.
 *
 * Воронка V2 (4 стадии продаж): new_lead → conversation → proposal_sent → won.
 *
 * По текущей стадии (deal.stage_key) показывает:
 * - Заголовок этапа + цель + бейдж фазы
 * - Чек-лист «Что сделать» (playbook.do[])
 * - Скрипт разговора (CallScript, раскрыт на sales-стадиях)
 * - Инструмент этапа (кнопки/форма/памятка по stage_key)
 * - Кнопка «Следующий этап» (с учётом гейт-локов; на conversation/proposal_sent — свои кнопки исхода)
 *
 * НЕ дублирует: PaymentBlock, Timeline — они остаются в ContactPage.
 */

import { useEffect, useState } from 'react'
import {
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Lock,
  Loader2,
  AlertTriangle,
  FileText,
  ServerCog,
  TestTube2,
  Target,
  CheckCheck,
  Inbox,
  BookOpen,
  Rocket,
  Clock,
  Zap,
  PackageCheck,
  Radio,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { CallScript } from '@/components/CallScript'
import { ChannelsTestReport } from '@/components/ChannelsTestReport'
import { ConfigurationForm } from '@/components/ConfigurationForm'
import { KbReview } from '@/components/KbReview'
import { usePlaybook } from '@/hooks/usePlaybook'
import {
  useMoveStage,
  usePipeline,
  dealKeys,
} from '@/hooks/useDeals'
import { useQueryClient } from '@tanstack/react-query'
import { formatRub } from '@/lib/utils'
import type {
  CalcPrice,
  Deal,
  DealStep,
  GateKey,
  ProposalResult,
  StageKey,
  TestRunResult,
  ProvisionClientResult,
} from '@/types'
import {
  invoiceApi,
  kbApi,
  leadsApi,
  proposalApi,
  testRunApi,
  provisionApi,
  dealsApi,
} from '@/api/client'

// ---- Phase badge ----

const PHASE_BADGE: Record<string, { label: string; variant: 'secondary' | 'gate_passed' | 'urgency_yellow' | 'repeat' }> = {
  sales: { label: 'Продажа', variant: 'urgency_yellow' },
  delivery: { label: 'Внедрение', variant: 'secondary' },
  account: { label: 'Сопровождение', variant: 'gate_passed' },
  terminal: { label: 'Завершено', variant: 'secondary' },
}

const GATE_LABELS: Record<GateKey, string> = {
  gate_1: 'Ворота 1',
  gate_2: 'Ворота 2 — Одобрение БЗ и промпт',
  gate_test: 'Тест-гейт ≥27/30',
  gate_3: 'Ворота 3 — Прод-запуск',
}

// Источник лида — человекочитаемо
const SOURCE_LABELS: Record<string, string> = {
  web_form: 'Сайт (форма)',
  telegram: 'Telegram',
  max: 'MAX',
  vk: 'ВКонтакте',
  avito: 'Avito',
  manual: 'Вручную',
}

// ---- Props ----

interface StageCockpitProps {
  deal: Deal
  steps: DealStep[]
  savedCalcPrice?: CalcPrice | null
  onGateError: (msg: string) => void
  onSuccess: (msg: string) => void
  onRefresh: () => void
}

// ---- Stage instrument: renders the tool for the current stage ----

function StageInstrument({
  deal,
  onGateError,
  onSuccess,
  onRefresh,
  onMoveStage,
  transitioning,
}: {
  deal: Deal
  onGateError: (msg: string) => void
  onSuccess: (msg: string) => void
  onRefresh: () => void
  onMoveStage: (key: StageKey) => void
  transitioning: StageKey | null
}) {
  const { data: pipeline } = usePipeline()
  const instrumentQc = useQueryClient()
  const USE_MOCK = import.meta.env.VITE_USE_MOCK != null
  ? import.meta.env.VITE_USE_MOCK === 'true'
  : import.meta.env.DEV

  const [proposalLoading, setProposalLoading] = useState(false)
  const [proposalResult, setProposalResult] = useState<ProposalResult | null>(null)
  const [invoiceLoading, setInvoiceLoading] = useState(false)
  const [testScore, setTestScore] = useState('')
  const [testNotes, setTestNotes] = useState('')
  const [testLoading, setTestLoading] = useState(false)
  const [testResult, setTestResult] = useState<TestRunResult | null>(null)
  const [showProvisionForm, setShowProvisionForm] = useState(false)
  const [provisionLoading, setProvisionLoading] = useState(false)
  const [provisionResult, setProvisionResult] = useState<ProvisionClientResult | null>(null)
  const [provisionName, setProvisionName] = useState('')
  const [provisionChannel, setProvisionChannel] = useState<'telegram' | 'max'>('telegram')
  const [provisionBotToken, setProvisionBotToken] = useState('')
  const [provisionMaxToken, setProvisionMaxToken] = useState('')
  const [provisionChatIds, setProvisionChatIds] = useState('')

  // ---- build ----
  const [kbBuildLoading, setKbBuildLoading] = useState(false)
  const [kbApproveLoading, setKbApproveLoading] = useState(false)

  // ---- integration ----
  const [channelsTestLoading, setChannelsTestLoading] = useState(false)
  const [channelsVerifiedLoading, setChannelsVerifiedLoading] = useState(false)
  const [channelsTestRun, setChannelsTestRun] = useState(0)

  // ---- launch ----
  const [ownerAcceptedLoading, setOwnerAcceptedLoading] = useState(false)
  const [cutoverLoading, setCutoverLoading] = useState(false)
  // Приёмка владельцем персистится в deal.config на бэкенде — инициализируем из неё.
  const [ownerAccepted, setOwnerAccepted] = useState<boolean>(
    Boolean((deal.config as Record<string, unknown> | undefined)?.owner_accepted),
  )

  // ---- hypercare ----
  const [invoice2Loading, setInvoice2Loading] = useState(false)

  // Синхронизируем флаг приёмки при обновлении сделки (refetch после действия/refresh).
  useEffect(() => {
    setOwnerAccepted(Boolean((deal.config as Record<string, unknown> | undefined)?.owner_accepted))
  }, [deal.config])

  const stage = deal.stage_key

  // ---- new_lead (сливает «Новая заявка» + «Эскалация от Тони») ----
  if (stage === 'new_lead') {
    const isClaimed = !!deal.claimed_by
    return (
      <div className="space-y-3">
        {/* Контекст лида — откуда пришёл, ниша, первое сообщение */}
        <div className="rounded-md border bg-slate-50 px-3 py-2.5 space-y-1.5 text-sm">
          <div className="flex items-center gap-2">
            <Inbox className="h-3.5 w-3.5 text-slate-400 shrink-0" />
            <span className="text-muted-foreground">Источник:</span>
            <span className="font-medium">
              {SOURCE_LABELS[deal.source_channel ?? ''] ?? deal.source_channel ?? 'неизвестно'}
            </span>
          </div>
          {deal.contact?.business_niche && (
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Ниша:</span>
              <span>{deal.contact.business_niche}</span>
            </div>
          )}
          {deal.first_message && (
            <div className="flex items-start gap-2">
              <span className="text-muted-foreground shrink-0">Написал:</span>
              <span className="italic">«{deal.first_message}»</span>
            </div>
          )}
          <p className="text-xs text-muted-foreground pt-0.5">
            Полная переписка и события — в ленте ниже.
          </p>
        </div>

        <p className="text-sm text-muted-foreground">
          {isClaimed
            ? `Лид взят: ${deal.claimed_by}`
            : 'Лид свободен — возьмите в работу, чтобы никто другой не позвонил одновременно.'}
        </p>
        {!isClaimed ? (
          <Button
            size="sm"
            className="gap-2"
            onClick={() => {
              leadsApi
                .claim(deal.id)
                .then((res) => {
                  onRefresh()
                  if (res.ok) onSuccess('Лид взят в работу')
                  else onGateError(res.message ?? 'Лид уже взят другим оператором')
                })
                .catch(() => onGateError('Ошибка при взятии лида'))
            }}
          >
            <CheckCircle2 className="h-4 w-4" />
            Взял в работу
          </Button>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            {/* После взятия — «дальше»: начать разговор (гейта нет) */}
            <Button
              size="sm"
              className="gap-2"
              disabled={transitioning === 'conversation'}
              onClick={() => onMoveStage('conversation')}
            >
              {transitioning === 'conversation' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
              Начать разговор
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="gap-2 text-slate-500"
              onClick={() => {
                leadsApi
                  .release(deal.id)
                  .then(() => { onRefresh(); onSuccess('Лид отпущен') })
                  .catch(() => onGateError('Ошибка при отпуске лида'))
              }}
            >
              Отказ / отпустить
            </Button>
          </div>
        )}
      </div>
    )
  }

  // ---- conversation — разговор: конфигуратор + исход ----
  if (stage === 'conversation') {
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Соберите конфигурацию по ходу разговора — система посчитает цену. Назовите её клиенту и предложите заключить договор.
        </p>
        <ConfigurationForm dealId={deal.id} onSaved={() => onSuccess('Расчёт сохранён')} />

        {/* Как заключить — короткая памятка для менеджера */}
        <div className="rounded-md bg-blue-50 border border-blue-100 px-3 py-2 text-xs text-blue-800 space-y-1">
          <p className="font-medium">Как заключить договор</p>
          <p>Предоплата 50%, запуск 10–14 рабочих дней. Гарантия: не запустим — вернём предоплату.</p>
        </div>

        {/* Исход разговора */}
        <div className="border-t pt-3">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Итог разговора</p>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              className="gap-2"
              disabled={transitioning === 'won'}
              onClick={() => onMoveStage('won')}
            >
              {transitioning === 'won' ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              Закрыли — Договор
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="gap-2"
              disabled={transitioning === 'proposal_sent'}
              onClick={() => onMoveStage('proposal_sent')}
            >
              {transitioning === 'proposal_sent' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
              Не закрыли — отправить КП
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="gap-2 text-slate-500"
              disabled={transitioning === 'lost'}
              onClick={() => onMoveStage('lost')}
            >
              Отказ
            </Button>
          </div>
        </div>
      </div>
    )
  }

  // ---- proposal_sent (только кто НЕ закрылся на звонке) ----
  if (stage === 'proposal_sent') {
    async function handleProposal() {
      setProposalLoading(true)
      try {
        let result: ProposalResult
        if (USE_MOCK) {
          result = {
            title: `КП: ${deal.contact?.display_name ?? deal.title}`,
            md: `# Коммерческое предложение\n\n**Клиент:** ${deal.contact?.display_name ?? deal.title}\n**Ниша:** ${deal.contact?.business_niche ?? '—'}\n\n## Стоимость\n\n| Статья | Сумма |\n|--------|-------|\n| Внедрение | ${formatRub(deal.value ?? 80000)} |\n| Подписка | 4 900 ₽/мес |\n\n---\n_Запуск 10–14 рабочих дней. Гарантия запуска. Предложение действует 14 дней._`,
          }
        } else {
          result = await proposalApi.generate(deal.id)
        }
        setProposalResult(result)
      } catch {
        onGateError('Ошибка при формировании КП')
      } finally {
        setProposalLoading(false)
      }
    }

    return (
      <div className="space-y-3">
        <div className="rounded-md bg-blue-50 border border-blue-100 px-3 py-2 text-sm text-blue-800">
          Клиент думает. Сформируйте КП, скачайте PDF и отправьте. Перезвоните через 1–2 дня.
        </div>
        <Button
          size="sm"
          variant="outline"
          className="gap-2"
          disabled={proposalLoading}
          onClick={handleProposal}
        >
          {proposalLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
          Сформировать КП
        </Button>
        {proposalResult && (
          <div className="rounded-md border bg-muted/40 p-3 space-y-2">
            <p className="text-sm font-medium">{proposalResult.title}</p>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                className="text-xs gap-1"
                onClick={() => window.open(proposalApi.getPdfUrl(deal.id), '_blank')}
              >
                <FileText className="h-3 w-3" />
                Скачать PDF
              </Button>
            </div>
            <div className="rounded-md border bg-background p-3 max-h-32 overflow-y-auto">
              <pre className="text-xs font-mono whitespace-pre-wrap">{proposalResult.md}</pre>
            </div>
          </div>
        )}

        {/* Итог: закрыли или отказ */}
        <div className="border-t pt-3 flex flex-wrap gap-2">
          <Button
            size="sm"
            className="gap-2"
            disabled={transitioning === 'won'}
            onClick={() => onMoveStage('won')}
          >
            {transitioning === 'won' ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
            Закрыли — Договор
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="gap-2 text-slate-500"
            disabled={transitioning === 'lost'}
            onClick={() => onMoveStage('lost')}
          >
            Отказ
          </Button>
        </div>
      </div>
    )
  }

  // ---- won ----
  if (stage === 'won') {
    const contractTotal = deal.value ?? 0
    const firstTranche = Math.round(contractTotal * 0.5)
    // Гейт «первый транш получен» = сумма достигнута. paid_at используем только для
    // отображения даты (не как часть условия) — иначе кнопка «Начать внедрение» не появляется,
    // т.к. бэк не всегда выставляет paid_at при первом транше.
    const firstPaid = contractTotal > 0 && deal.paid_amount >= firstTranche

    async function handleInvoice() {
      setInvoiceLoading(true)
      try {
        if (!USE_MOCK) await invoiceApi.generate(deal.id, 1)
        window.open(invoiceApi.getPdfUrl(deal.id, 1), '_blank')
        onSuccess('Счёт сформирован')
      } catch {
        onGateError('Ошибка при формировании счёта')
      } finally {
        setInvoiceLoading(false)
      }
    }

    const nextStage = pipeline?.stages.find((s) => s.key === 'onboarding')
    const onboardingGate = nextStage?.gate_key
      ? deal.gates?.find((g) => g.gate_key === nextStage.gate_key)?.result
      : 'passed'

    return (
      <div className="space-y-3">
        {contractTotal > 0 && (
          <div className="text-sm font-medium">
            Сумма договора: {formatRub(contractTotal)} · Первый транш: {formatRub(firstTranche)}
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            className="gap-2 text-xs"
            disabled={invoiceLoading}
            onClick={handleInvoice}
          >
            {invoiceLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileText className="h-3 w-3" />}
            Сформировать счёт (1-й транш)
          </Button>
        </div>
        {/* Отметку оплаты держим в одном месте — блок «Оплата 50/50» ниже,
            чтобы не было двух конкурирующих кнопок и рассинхрона до рефетча. */}
        {!firstPaid ? (
          <p className="text-xs text-muted-foreground">
            Отметьте получение 1-го транша в блоке «Оплата 50/50» ниже — после этого появится «Начать внедрение».
          </p>
        ) : (
          <div className="flex items-center gap-1.5 text-sm text-green-700">
            <CheckCircle2 className="h-4 w-4" />
            1-й транш получен
          </div>
        )}
        {firstPaid && (
          <Button
            size="sm"
            className="gap-2"
            disabled={onboardingGate !== 'passed' && onboardingGate !== undefined || transitioning === 'onboarding'}
            onClick={() => onMoveStage('onboarding')}
          >
            {transitioning === 'onboarding' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            Начать внедрение
          </Button>
        )}
      </div>
    )
  }

  // ---- onboarding ----
  if (stage === 'onboarding') {
    function getDefaultName() {
      const name = deal.contact?.display_name ?? ''
      const niche = deal.contact?.business_niche ?? ''
      return niche ? `${name} / ${niche}` : name
    }

    async function handleProvision(e: React.FormEvent) {
      e.preventDefault()
      setProvisionLoading(true)
      try {
        const chatIds = provisionChatIds.split(',').map((s) => s.trim()).filter(Boolean)
        let result: ProvisionClientResult
        if (USE_MOCK) {
          result = { client_tenant_id: `tenant-${Date.now()}`, created: true }
        } else {
          result = await provisionApi.provision(deal.id, {
            name: provisionName || getDefaultName() || undefined,
            channel: provisionChannel,
            bot_token: provisionBotToken || undefined,
            max_bot_token: provisionMaxToken || undefined,
            operator_chat_ids: chatIds.length > 0 ? chatIds : undefined,
          })
        }
        setProvisionResult(result)
        setShowProvisionForm(false)
        onSuccess(`Кабинет создан: ${result.client_tenant_id}`)
        onRefresh()
      } catch {
        onGateError('Ошибка при создании кабинета')
      } finally {
        setProvisionLoading(false)
      }
    }

    return (
      <div className="space-y-3">
        {provisionResult ? (
          <div className="rounded-md bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-800">
            <CheckCircle2 className="h-3 w-3 inline mr-1" />
            Кабинет создан: <strong>{provisionResult.client_tenant_id}</strong>
          </div>
        ) : null}
        {!showProvisionForm ? (
          <Button
            size="sm"
            variant="outline"
            className="gap-2"
            onClick={() => { setProvisionName(getDefaultName()); setShowProvisionForm(true) }}
          >
            <ServerCog className="h-4 w-4" />
            Создать кабинет клиента
          </Button>
        ) : (
          <form onSubmit={handleProvision} className="space-y-2 p-3 rounded-md border bg-muted/40">
            <p className="text-xs font-medium">Создать кабинет клиента</p>
            <input
              type="text"
              className="w-full text-xs rounded border px-2 py-1.5 bg-background"
              value={provisionName}
              onChange={(e) => setProvisionName(e.target.value)}
              placeholder={getDefaultName()}
            />
            <select
              className="w-full text-xs rounded border px-2 py-1.5 bg-background"
              value={provisionChannel}
              onChange={(e) => setProvisionChannel(e.target.value as 'telegram' | 'max')}
            >
              <option value="telegram">Telegram</option>
              <option value="max">VK Max</option>
            </select>
            <input
              type="text"
              className="w-full text-xs rounded border px-2 py-1.5 bg-background font-mono"
              value={provisionBotToken}
              onChange={(e) => setProvisionBotToken(e.target.value)}
              placeholder="Bot token (опционально)"
            />
            <input
              type="text"
              className="w-full text-xs rounded border px-2 py-1.5 bg-background font-mono"
              value={provisionMaxToken}
              onChange={(e) => setProvisionMaxToken(e.target.value)}
              placeholder="Токен MAX-бота клиента (опционально)"
            />
            <input
              type="text"
              className="w-full text-xs rounded border px-2 py-1.5 bg-background font-mono"
              value={provisionChatIds}
              onChange={(e) => setProvisionChatIds(e.target.value)}
              placeholder="Chat IDs (через запятую)"
            />
            <div className="flex gap-2">
              <Button type="submit" size="sm" className="text-xs" disabled={provisionLoading}>
                {provisionLoading && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
                Создать
              </Button>
              <Button type="button" size="sm" variant="outline" className="text-xs" onClick={() => setShowProvisionForm(false)}>
                Отмена
              </Button>
            </div>
          </form>
        )}
      </div>
    )
  }

  // ---- testing ----
  if (stage === 'testing') {
    async function handleTestRun(e: React.FormEvent) {
      e.preventDefault()
      const score = parseInt(testScore, 10)
      if (isNaN(score) || score < 0 || score > 30) {
        onGateError('Введите балл от 0 до 30')
        return
      }
      setTestLoading(true)
      try {
        let result: TestRunResult
        if (USE_MOCK) {
          result = { test_run_id: `tr-${Date.now()}`, pass_: score >= 27, score, max: 30, kb_content_hash: 'abc' }
        } else {
          result = await testRunApi.record(deal.id, { score, max: 30, notes: testNotes || undefined })
        }
        setTestResult(result)
        if (result.pass_) {
          onSuccess(`Тест пройден: ${score}/30`)
        }
      } catch {
        onGateError('Ошибка при записи теста')
      } finally {
        setTestLoading(false)
      }
    }

    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">Введите результат тест-прогона. Порог — 27/30 баллов.</p>
        <form onSubmit={handleTestRun} className="space-y-2">
          <div className="flex items-center gap-3">
            <label className="text-xs text-muted-foreground w-24 shrink-0">Балл (0–30)</label>
            <input
              type="number"
              min={0}
              max={30}
              className="w-20 text-sm rounded border px-2 py-1.5 bg-background"
              value={testScore}
              onChange={(e) => setTestScore(e.target.value)}
              placeholder="27"
            />
          </div>
          <textarea
            className="w-full text-xs rounded border px-2 py-1.5 bg-background resize-none"
            rows={2}
            value={testNotes}
            onChange={(e) => setTestNotes(e.target.value)}
            placeholder="Заметки (опционально)"
          />
          <Button type="submit" size="sm" variant="outline" className="gap-2" disabled={testLoading}>
            {testLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <TestTube2 className="h-3 w-3" />}
            Записать результат
          </Button>
        </form>
        {testResult && (
          <div className={`rounded-md border px-3 py-2 text-sm ${testResult.pass_ ? 'bg-green-50 border-green-200 text-green-800' : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
            {testResult.pass_
              ? `Тест-гейт пройден (${testResult.score}/${testResult.max}) — можно запускать`
              : `Ниже порога (${testResult.score}/${testResult.max}) — вернуть на доработку`}
          </div>
        )}

        {/* Правка БЗ и промпта прямо с тестирования: увидел плохой ответ — исправил
            здесь же, не уходя со стадии. Правка топика возвращает Ворота 2 в
            «не пройдены», а тест-гейт слетает по расхождению хэша БЗ — это
            задумано, после правки прогон нужен заново. */}
        <div className="pt-2 border-t">
          <KbReview dealId={deal.id} onEdited={onRefresh} />
        </div>

        <Button
          size="sm"
          variant="outline"
          className="gap-2 text-xs text-muted-foreground"
          disabled={transitioning === 'build'}
          onClick={() => onMoveStage('build')}
        >
          {transitioning === 'build' ? <Loader2 className="h-3 w-3 animate-spin" /> : <ChevronLeft className="h-3 w-3" />}
          Вернуть в «Сборка БЗ + промпт»
        </Button>
      </div>
    )
  }

  // ---- build (Сборка БЗ + промпт) ----
  if (stage === 'build') {
    const gate2 = deal.gates?.find((g) => g.gate_key === 'gate_2')
    const gate2Passed = gate2?.result === 'passed'

    async function handleKbBuild() {
      setKbBuildLoading(true)
      try {
        if (!USE_MOCK) {
          await dealsApi.action(deal.id, { label: 'Собрать БЗ', kind: 'kb_build' })
        }
        // Воркер собирает топики асинхронно — панель чтения обновится по
        // возвращении на карточку или после ручного обновления страницы.
        instrumentQc.invalidateQueries({ queryKey: ['kb', deal.id] })
        onSuccess('Задача «Собрать БЗ» поставлена в очередь')
      } catch {
        onGateError('Ошибка при запуске сборки БЗ')
      } finally {
        setKbBuildLoading(false)
      }
    }

    async function handleKbApprove() {
      setKbApproveLoading(true)
      try {
        if (!USE_MOCK) {
          await kbApi.approve(deal.id)
        }
        instrumentQc.invalidateQueries({ queryKey: ['kb', deal.id] })
        onSuccess('БЗ одобрена — Ворота 2 пройдены')
        onRefresh()
      } catch {
        onGateError('Ошибка при одобрении БЗ')
      } finally {
        setKbApproveLoading(false)
      }
    }

    return (
      <div className="space-y-3">
        {/* Статус gate_2 */}
        <div className={`rounded-md border px-3 py-2 text-sm flex items-center gap-2 ${gate2Passed ? 'bg-green-50 border-green-200 text-green-800' : 'bg-slate-50 border-slate-200 text-slate-700'}`}>
          {gate2Passed
            ? <CheckCircle2 className="h-4 w-4 shrink-0" />
            : <Lock className="h-4 w-4 shrink-0 text-amber-500" />}
          <span>
            {gate2Passed
              ? 'Ворота 2 пройдены — база знаний одобрена'
              : (gate2?.result === 'failed'
                ? 'Ворота 2: не пройдены (топики отклонены)'
                : 'Ворота 2: база знаний ждёт одобрения')}
          </span>
        </div>

        {/* Памятка */}
        <div className="rounded-md bg-blue-50 border border-blue-100 px-3 py-2 text-xs text-blue-800 space-y-1">
          <p className="font-medium flex items-center gap-1"><BookOpen className="h-3.5 w-3.5" /> Что нужно для Ворот 2</p>
          <p>Обязателен системный промпт — он задаёт поведение агента. Разделы с фактурой (о компании, цены, FAQ и свои) добавляйте в любом количестве, прочитайте их и согласуйте с клиентом.</p>
        </div>

        {/* Чтение и правка БЗ — прямо над кнопкой одобрения: одобряем прочитанное.
            Правка после одобрения закрывает Ворота 2 (сервер возвращает их в
            «не пройдены») — обновляем карточку, чтобы статус не врал. */}
        <KbReview dealId={deal.id} onEdited={onRefresh} />

        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            className="gap-2 text-xs"
            disabled={kbBuildLoading}
            onClick={handleKbBuild}
          >
            {kbBuildLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Zap className="h-3 w-3" />}
            Собрать БЗ
          </Button>
          {!gate2Passed && (
            <Button
              size="sm"
              variant="outline"
              className="gap-2 text-xs"
              disabled={kbApproveLoading}
              onClick={handleKbApprove}
            >
              {kbApproveLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
              Одобрить БЗ
            </Button>
          )}
        </div>

        <div className="border-t pt-3">
          <Button
            size="sm"
            className="gap-2"
            disabled={!gate2Passed || transitioning === 'integration'}
            title={!gate2Passed ? 'Сначала одобрите БЗ (Ворота 2)' : undefined}
            onClick={() => gate2Passed && onMoveStage('integration')}
          >
            {transitioning === 'integration' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : !gate2Passed ? (
              <Lock className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            Перейти: Подключение каналов
          </Button>
          {!gate2Passed && (
            <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3 text-amber-500" />
              Одобрите БЗ, чтобы разблокировать переход
            </p>
          )}
        </div>
      </div>
    )
  }

  // ---- integration (Подключение каналов) ----
  if (stage === 'integration') {
    const channels = (deal.config?.channels as string[] | undefined) ?? []

    async function handleChannelsTest() {
      setChannelsTestLoading(true)
      try {
        if (!USE_MOCK) {
          await dealsApi.action(deal.id, { label: 'Автотест каналов', kind: 'channels_test' })
        }
        // Воркер отработает за ~5 секунд — отчёт ниже сам подтянется.
        setChannelsTestRun((n) => n + 1)
        onSuccess('Автотест каналов запущен — отчёт появится ниже')
      } catch {
        onGateError('Ошибка при запуске автотеста каналов')
      } finally {
        setChannelsTestLoading(false)
      }
    }

    async function handleChannelsVerified() {
      setChannelsVerifiedLoading(true)
      try {
        if (!USE_MOCK) {
          await dealsApi.action(deal.id, { label: 'Каналы проверены', kind: 'channels_verified' })
        }
        onSuccess('Статус «Каналы проверены» зафиксирован')
        onRefresh()
      } catch (e) {
        // Сервер отказывает с причиной (нет автотеста / тест провален) —
        // показываем её как есть, а не общее «что-то пошло не так».
        onGateError(
          e instanceof Error && e.message
            ? e.message
            : 'Ошибка при фиксации статуса каналов'
        )
      } finally {
        setChannelsVerifiedLoading(false)
      }
    }

    return (
      <div className="space-y-3">
        {/* Список каналов из конфига */}
        {channels.length > 0 && (
          <div className="rounded-md border bg-slate-50 px-3 py-2 space-y-1">
            <p className="text-xs font-medium text-slate-600 mb-1.5">Каналы в проекте</p>
            {channels.map((ch) => (
              <div key={ch} className="flex items-center gap-2 text-sm text-slate-700">
                <Radio className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                <span>{ch}</span>
              </div>
            ))}
          </div>
        )}

        {/* Отчёт последнего автотеста — иначе кнопка запускает проверку «в никуда» */}
        <ChannelsTestReport dealId={deal.id} refreshKey={channelsTestRun} />

        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            className="gap-2 text-xs"
            disabled={channelsTestLoading}
            onClick={handleChannelsTest}
          >
            {channelsTestLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <TestTube2 className="h-3 w-3" />}
            Автотест каналов
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="gap-2 text-xs"
            disabled={channelsVerifiedLoading}
            onClick={handleChannelsVerified}
          >
            {channelsVerifiedLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
            Каналы проверены
          </Button>
        </div>

        <div className="border-t pt-3 flex flex-wrap gap-2">
          <Button
            size="sm"
            className="gap-2"
            disabled={!!transitioning}
            onClick={() => onMoveStage('testing')}
          >
            {transitioning === 'testing' ? <Loader2 className="h-4 w-4 animate-spin" /> : <ChevronRight className="h-4 w-4" />}
            Перейти: Тестирование
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="gap-2 text-xs text-muted-foreground"
            disabled={!!transitioning}
            onClick={() => onMoveStage('build')}
          >
            {transitioning === 'build' ? <Loader2 className="h-3 w-3 animate-spin" /> : <ChevronLeft className="h-3 w-3" />}
            Вернуть в «Сборка БЗ + промпт»
          </Button>
        </div>
      </div>
    )
  }

  // ---- launch (Запуск) ----
  if (stage === 'launch') {
    const gate3 = deal.gates?.find((g) => g.gate_key === 'gate_3')
    const gate3Passed = gate3?.result === 'passed'

    // gate_3 требует: provision-client (кабинет) + owner_bot (бот владельца).
    // Если gate_3 нет или pending — подсказываем что проверить.
    const canCutover = ownerAccepted && gate3Passed

    async function handleOwnerAccepted() {
      setOwnerAcceptedLoading(true)
      try {
        if (!USE_MOCK) {
          await dealsApi.action(deal.id, { label: 'Владелец принял', kind: 'owner_accepted' })
        }
        setOwnerAccepted(true)
        onSuccess('Демо принято владельцем — зафиксировано')
      } catch {
        onGateError('Ошибка при фиксации приёмки')
      } finally {
        setOwnerAcceptedLoading(false)
      }
    }

    async function handleCutover() {
      setCutoverLoading(true)
      try {
        if (!USE_MOCK) {
          await dealsApi.action(deal.id, { label: 'Включить в прод', kind: 'channels_cutover' })
        }
        onSuccess('Переключение на прод запущено')
        onMoveStage('hypercare')
      } catch (err: unknown) {
        const apiErr = (err as { apiError?: { detail?: string; code?: string; error?: string } }).apiError
        if (apiErr?.detail === 'client_not_provisioned') {
          onGateError('Кабинет клиента не создан — вернитесь в Онбординг и выполните «Создать кабинет клиента»')
        } else if (apiErr?.detail === 'owner_bot_not_configured') {
          onGateError('Бот владельца не настроен — укажите токен и chat_id в Онбординге')
        } else {
          onGateError('Ошибка при переключении на прод')
        }
      } finally {
        setCutoverLoading(false)
      }
    }

    return (
      <div className="space-y-3">
        {/* Статус gate_3 */}
        <div className={`rounded-md border px-3 py-2 text-sm ${gate3Passed ? 'bg-green-50 border-green-200 text-green-800' : 'bg-amber-50 border-amber-100 text-amber-800'}`}>
          <div className="flex items-center gap-2">
            {gate3Passed
              ? <CheckCircle2 className="h-4 w-4 shrink-0" />
              : <AlertTriangle className="h-4 w-4 shrink-0" />}
            <span className="font-medium">
              {gate3Passed ? 'Ворота 3 пройдены — прод-запуск разрешён' : 'Ворота 3 не пройдены'}
            </span>
          </div>
          {!gate3Passed && (
            <ul className="mt-1.5 ml-6 space-y-0.5 text-xs list-disc">
              <li>Кабинет клиента создан? (Онбординг → «Создать кабинет клиента»)</li>
              <li>Бот владельца настроен? (токен + operator_chat_ids в Онбординге)</li>
            </ul>
          )}
        </div>

        {/* Чекбокс приёмки владельцем */}
        <div className="flex items-center gap-3 rounded-md border px-3 py-2.5">
          <button
            type="button"
            className={`h-5 w-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${ownerAccepted ? 'bg-green-600 border-green-600' : 'border-slate-300 hover:border-slate-400'}`}
            onClick={ownerAccepted ? undefined : handleOwnerAccepted}
            disabled={ownerAcceptedLoading || ownerAccepted}
            aria-label="Владелец принял демо"
          >
            {ownerAcceptedLoading
              ? <Loader2 className="h-3 w-3 animate-spin text-slate-500" />
              : ownerAccepted
                ? <CheckCircle2 className="h-3.5 w-3.5 text-white" />
                : null}
          </button>
          <div>
            <p className="text-sm font-medium">Владелец принял (демо проведено)</p>
            <p className="text-xs text-muted-foreground">Нажмите чекбокс после согласования с клиентом</p>
          </div>
        </div>

        {/* Кнопка cutover */}
        <div className="flex flex-col gap-1.5">
          <Button
            size="sm"
            className="gap-2 self-start"
            disabled={!canCutover || cutoverLoading}
            title={!ownerAccepted ? 'Сначала отметьте приёмку владельцем' : !gate3Passed ? 'Ворота 3 не пройдены' : undefined}
            onClick={canCutover ? handleCutover : undefined}
          >
            {cutoverLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : !canCutover ? (
              <Lock className="h-4 w-4" />
            ) : (
              <Rocket className="h-4 w-4" />
            )}
            Включить в прод
          </Button>
          {!ownerAccepted && (
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <AlertTriangle className="h-3 w-3 text-amber-500" />
              Отметьте приёмку владельца выше
            </p>
          )}
          {ownerAccepted && !gate3Passed && (
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <AlertTriangle className="h-3 w-3 text-amber-500" />
              Ворота 3 не пройдены — проверьте кабинет и бот владельца
            </p>
          )}
        </div>
      </div>
    )
  }

  // ---- hypercare (Сопровождение 14 дней) ----
  if (stage === 'hypercare') {
    const contractTotal = deal.value ?? 0
    const secondTranche = Math.round(contractTotal * 0.5)

    // Обратный отсчёт до second_tranche_due
    let daysLeft: number | null = null
    if (deal.second_tranche_due) {
      const due = new Date(deal.second_tranche_due)
      const now = new Date()
      daysLeft = Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
    }

    async function handleInvoice2() {
      setInvoice2Loading(true)
      try {
        if (!USE_MOCK) await invoiceApi.generate(deal.id, 2)
        window.open(invoiceApi.getPdfUrl(deal.id, 2), '_blank')
        onSuccess('Счёт на 2-й транш сформирован')
      } catch {
        onGateError('Ошибка при формировании счёта на 2-й транш')
      } finally {
        setInvoice2Loading(false)
      }
    }

    return (
      <div className="space-y-3">
        {/* Обратный отсчёт */}
        {daysLeft !== null && (
          <div className={`rounded-md border px-3 py-2.5 flex items-center gap-2 text-sm ${daysLeft <= 3 ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-slate-50 border-slate-200 text-slate-700'}`}>
            <Clock className="h-4 w-4 shrink-0" />
            <span>
              {daysLeft > 0
                ? `До 2-го транша: ${daysLeft} дн. (${new Date(deal.second_tranche_due!).toLocaleDateString('ru-RU')})`
                : daysLeft === 0
                  ? 'Сегодня — дата 2-го транша'
                  : `Срок 2-го транша истёк ${Math.abs(daysLeft)} дн. назад`}
            </span>
          </div>
        )}
        {!deal.second_tranche_due && (
          <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-600">
            Дата 2-го транша не указана. Установите её в блоке «Оплата 50/50» ниже.
          </div>
        )}

        {/* Сумма + кнопка счёта */}
        {contractTotal > 0 && (
          <div className="text-sm font-medium">
            2-й транш: {secondTranche.toLocaleString('ru-RU')} ₽
          </div>
        )}
        <Button
          size="sm"
          variant="outline"
          className="gap-2 text-xs"
          disabled={invoice2Loading}
          onClick={handleInvoice2}
        >
          {invoice2Loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileText className="h-3 w-3" />}
          Выставить счёт (2-й транш)
        </Button>

        {/* Памятка */}
        <div className="rounded-md bg-blue-50 border border-blue-100 px-3 py-2 text-xs text-blue-800 space-y-1">
          <p className="font-medium flex items-center gap-1"><PackageCheck className="h-3.5 w-3.5" /> Задачи гиперкэра</p>
          <p>Сопровождение 14 дней: ежедневные чек-ины, разбор диалогов Тони, корректировка промпта. После оплаты 2-го транша — перевод в «Активен».</p>
        </div>

        <div className="border-t pt-3">
          <Button
            size="sm"
            className="gap-2"
            disabled={!!transitioning}
            onClick={() => onMoveStage('active')}
          >
            {transitioning === 'active' ? <Loader2 className="h-4 w-4 animate-spin" /> : <ChevronRight className="h-4 w-4" />}
            Перейти: Активен
          </Button>
        </div>
      </div>
    )
  }

  // ---- Прочие стадии — общая памятка ----
  return (
    <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-3 space-y-2 text-sm text-slate-600">
      <p>Выполните задачи из чек-листа выше и переходите на следующую стадию.</p>
    </div>
  )
}

// ---- Main StageCockpit ----

export function StageCockpit({
  deal,
  savedCalcPrice,
  onGateError,
  onSuccess,
  onRefresh,
}: StageCockpitProps) {
  const { data: playbook } = usePlaybook()
  const { data: pipeline } = usePipeline()
  const moveStage = useMoveStage()
  const qc = useQueryClient()
  const [transitioning, setTransitioning] = useState<StageKey | null>(null)

  const stage = deal.stage_key
  const pbStage = playbook?.[stage]

  const currentPipelineStage = pipeline?.stages.find((s) => s.key === stage)
  const phase = currentPipelineStage?.phase ?? 'sales'
  const phaseBadge = PHASE_BADGE[phase] ?? PHASE_BADGE.sales

  // Determine primary next stage
  const allowedNext = currentPipelineStage?.allowed_next ?? []
  const primaryNext = allowedNext[0] ? pipeline?.stages.find((s) => s.key === allowedNext[0]) : null

  function isNextLocked(nextKey: StageKey): boolean {
    const nextStage = pipeline?.stages.find((s) => s.key === nextKey)
    if (!nextStage?.gate_key) return false
    const gate = deal.gates?.find((g) => g.gate_key === nextStage.gate_key)
    return !gate || gate.result !== 'passed'
  }

  async function handleMove(targetKey: StageKey) {
    setTransitioning(targetKey)
    try {
      await moveStage.mutateAsync({ dealId: deal.id, stageKey: targetKey })
      onRefresh()
      qc.invalidateQueries({ queryKey: dealKeys.detail(deal.id) })
    } catch (err: unknown) {
      const apiErr = (err as { apiError?: { error: string; gate?: string; code?: string; detail?: string } }).apiError
      if (apiErr?.detail === 'client_not_provisioned') {
        onGateError('Сначала создайте кабинет клиента')
      } else if (apiErr?.detail === 'owner_bot_not_configured') {
        onGateError('Укажите токен бота и chat_id операторов')
      } else if (apiErr?.error === 'gate_blocked' || apiErr?.code === 'gate_blocked') {
        onGateError(`Гейт не пройден: ${apiErr.gate ? GATE_LABELS[apiErr.gate as GateKey] ?? apiErr.gate : 'условие перехода'}`)
      } else if (apiErr?.error === 'transition_not_allowed' || apiErr?.code === 'transition_not_allowed') {
        onGateError('Этот переход не разрешён')
      } else {
        onGateError('Ошибка при смене стадии')
      }
    } finally {
      setTransitioning(null)
    }
  }

  // Sales stages where script is open by default
  const SALES_STAGES: StageKey[] = ['new_lead', 'conversation', 'proposal_sent', 'won']
  const scriptDefaultOpen = SALES_STAGES.includes(stage)

  const doList = pbStage?.do ?? []
  const hasDoList = doList.length > 0
  const hasScript = pbStage?.script &&
    (pbStage.script.open || pbStage.script.questions.length > 0 || pbStage.script.pitch || pbStage.script.close)

  return (
    <Card className="border-2 border-primary/20">
      <CardHeader className="pb-3">
        {/* Stage header */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant={phaseBadge.variant}>{phaseBadge.label}</Badge>
              <span className="text-xs text-muted-foreground">›</span>
              <span className="text-lg font-semibold text-foreground">
                {pbStage?.title ?? currentPipelineStage?.name ?? stage}
              </span>
            </div>
            {pbStage?.goal && (
              <div className="mt-1.5 flex items-start gap-1.5">
                <Target className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
                <p className="text-sm text-muted-foreground">{pbStage.goal}</p>
              </div>
            )}
          </div>
          {/* Saved calc price summary */}
          {savedCalcPrice && (
            <div className="text-right shrink-0">
              <p className="text-xs text-muted-foreground">Договор</p>
              <p className="text-base font-bold text-foreground">{formatRub(savedCalcPrice.contract_total)}</p>
              <p className="text-xs text-muted-foreground">{formatRub(savedCalcPrice.first_tranche)} / {formatRub(savedCalcPrice.second_tranche)}</p>
            </div>
          )}
        </div>

        {/* Fallback if no playbook for this stage */}
        {!pbStage && (
          <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground rounded-md bg-slate-50 border px-3 py-2">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
            Для этой стадии нет инструкций в плейбуке
          </div>
        )}
      </CardHeader>

      <CardContent className="space-y-5">
        {/* Чек-лист «Что сделать» */}
        {hasDoList && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <CheckCheck className="h-4 w-4 text-slate-500" />
              <p className="text-sm font-semibold text-slate-700">Что сделать</p>
            </div>
            <ul className="space-y-1.5">
              {doList.map((item, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-slate-700">
                  <span className="mt-1 h-4 w-4 rounded-full border-2 border-slate-300 shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Скрипт разговора */}
        {hasScript && (
          <CallScript
            script={pbStage!.script}
            defaultOpen={scriptDefaultOpen}
          />
        )}

        {/* Инструмент этапа */}
        <div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Инструмент этапа</p>
          <StageInstrument
            deal={deal}
            onGateError={onGateError}
            onSuccess={onSuccess}
            onRefresh={onRefresh}
            onMoveStage={handleMove}
            transitioning={transitioning}
          />
        </div>

        {/* Кнопка следующего этапа (для стадий, где instrument не показывает её сам).
            Перечисленные стадии управляют переходами самостоятельно — не дублируем. */}
        {primaryNext &&
          stage !== 'won' &&
          stage !== 'new_lead' &&
          stage !== 'conversation' &&
          stage !== 'proposal_sent' &&
          stage !== 'build' &&
          stage !== 'integration' &&
          stage !== 'launch' &&
          stage !== 'hypercare' && (
            <div className="border-t pt-4">
              {(() => {
                const locked = isNextLocked(primaryNext.key)
                const gateKey = pipeline?.stages.find((s) => s.key === primaryNext.key)?.gate_key
                return (
                  <div className="space-y-1.5">
                    <Button
                      size="sm"
                      variant={locked ? 'outline' : 'default'}
                      disabled={locked || !!transitioning}
                      onClick={() => !locked && handleMove(primaryNext.key)}
                      className={`gap-2 ${locked ? 'opacity-60 cursor-not-allowed' : ''}`}
                      title={locked && gateKey ? `Гейт не пройден: ${GATE_LABELS[gateKey]}` : undefined}
                    >
                      {transitioning === primaryNext.key ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : locked ? (
                        <Lock className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                      Следующий этап: {primaryNext.name}
                    </Button>
                    {locked && gateKey && (
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3 text-amber-500" />
                        {GATE_LABELS[gateKey]}
                      </p>
                    )}
                  </div>
                )
              })()}
            </div>
          )}
      </CardContent>
    </Card>
  )
}
